/* 브라우저 현위치 추적.
 *
 * 카카오 SDK 에는 위치 API 가 없다 — 지도를 그릴 뿐이라 '내가 지금 어디인가'는 모른다.
 * 좌표는 브라우저(navigator.geolocation)가 주고, 우리는 그걸 kakao.maps.LatLng 로 옮겨
 * 지도에 얹는다. 카카오 공식 예제도 정확히 이 조합이다.
 *
 * 페이지가 뜨자마자 부르지 않는다. 권한 창은 사람이 '내 위치'를 누른 직후에 떠야 무엇을
 * 묻는 창인지 알 수 있다. 맥락 없이 뜬 창에서 한 번 거절당하면 그 도메인의 권한은 막힌
 * 채로 남고 코드로는 다시 물어볼 방법이 없다 — 사람이 브라우저 설정에서 직접 풀어야
 * 한다. 첫 요청은 반드시 클릭에서 시작한다(UserButton.jsx 의 팝업과 같은 규칙이다).
 *
 * https 가 아니면 동작하지 않는다. 크롬은 안전하지 않은 출처의 geolocation 을 묻지도
 * 않고 PERMISSION_DENIED 로 돌려보낸다 — localhost 만 예외다. 배포는 Firebase Hosting
 * (https)이라 상관없지만, 사내망 IP 로 직접 열어 시험하면 늘 거절당한다.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 이 기기에서 '내 위치'를 내놓을 것인가. 아니면 버튼 자체를 안 그린다 —
 * 눌러도 12초 뒤 "못 찾았다"만 나오는 버튼은 기능이 아니라 고장이다.
 *
 * **손에 들고 다니는 기기에서만 켠다.** 데스크톱은 GPS 가 없어서, 주변 Wi-Fi AP 를
 * 스캔해 애플·구글에 물어보는 경로 하나에 전부 매달린다. 그 경로는 OS 의 위치 서비스가
 * 브라우저에 열려 있어야 하는데 사무실 맥에서 닫혀 있는 일이 흔하고, 그러면 정확도를
 * 낮춘 요청까지 똑같이 kCLErrorLocationUnknown 으로 죽는다(실제로 겪었다). 브라우저
 * 권한과는 별개라 사이트에서 허용해도 소용이 없다. 애초에 회사 자리에 앉아 있는 사람에게
 * '내 위치'는 회사 표식과 같은 자리라 알려줄 것도 없다.
 *
 * 판별은 UA 문자열이 아니라 pointer: coarse 로 한다 — '주 포인터가 손가락인가'라는
 * 뜻이라 폰·태블릿은 참, 트랙패드 달린 터치 노트북은 거짓이다. UA 는 브라우저마다
 * 다르게 쓰고 주기적으로 바뀌지만 이건 기기의 성질이다.
 *
 * 모듈이 뜰 때 한 번만 본다(REDUCED_MOTION 과 같다). 세션 중에 기기 성질이 바뀔 일은
 * 없다 — 개발자도구의 기기 흉내를 켰다면 새로고침해야 반영된다.
 */
export const CAN_LOCATE =
  typeof navigator !== "undefined" &&
  "geolocation" in navigator &&
  matchMedia("(pointer: coarse)").matches;

/* 걸어 다니며 보는 화면이라 GPS 를 쓴다(enableHighAccuracy). maximumAge 는 10초 안쪽의
   직전 좌표를 그대로 받게 해준다 — 누르자마자 점이 뜨느냐 마느냐가 여기서 갈린다.
   timeout 은 일부러 안 준다. 포기 시점을 정하는 시계는 아래 GIVE_UP_MS 하나뿐이어야
   한다 — 둘을 같이 두면 어느 쪽이 먼저 울렸는지에 따라 화면이 달라진다. */
const OPTS = { enableHighAccuracy: true, maximumAge: 10_000 };

/* 첫 좌표를 기다려주는 시간.
 *
 * 오류 하나로는 접을지 말지 정할 수가 없다. 맥은 주변 Wi-Fi AP 를 스캔해 위치를 역산하는데,
 * 아직 못 골랐을 때 CoreLocation 이 kCLErrorLocationUnknown 을 **즉시** 던진다. 브라우저에는
 * POSITION_UNAVAILABLE 로 오고, 몇 초 뒤 좌표가 멀쩡히 따라오는 경우가 흔하다. 그런데 유선만
 * 꽂힌 맥(Wi-Fi 꺼짐)은 스캔할 게 없어서 같은 오류를 영영 던진다 — 코드가 같아서 구별이 안 된다.
 *
 * 그래서 오류를 보고 정하지 않고 시간을 보고 정한다. 이 안에 좌표가 오면 성공이고, 안 오면
 * 못 잡는 환경이다. 12초는 '고장난 줄 알고 다시 누를 때까지' 안쪽에서 최대한 기다린 값이다. */
const GIVE_UP_MS = 12_000;

/* 거친 좌표를 같이 부른다. 위의 정밀 요청과 **동시에** 나가고, 먼저 오는 쪽이 점을 띄운다.
 *
 * 정확도를 낮추면 CoreLocation 이 다른 경로를 탄다 — 정밀 경로는 데스크톱 맥에서 유독 잘
 * 실패하는데(GPS 가 없다), 거친 쪽은 Wi-Fi 로 역산한 값이나 캐시를 그냥 내준다. 실제로
 * kCLErrorLocationUnknown 이 반복되는 맥에서 이쪽만 성공하는 일이 흔하다.
 *
 * maximumAge 를 5분으로 크게 잡는다. 점심 자리를 고르는 데 5분 전 좌표는 충분하다 —
 * 그 사이 걸어봐야 몇백 미터고, 어차피 오차 반경이 그만큼 그려진다. 정밀 좌표가 뒤늦게
 * 오면 그게 덮는다. */
const COARSE = { enableHighAccuracy: false, maximumAge: 300_000, timeout: GIVE_UP_MS };

/* 권한을 푸는 자리가 기기마다 다르다.
   처음엔 "주소창의 자물쇠"라고만 적었다. 데스크톱을 보고 쓴 문장인데, 이 기능이 폰
   전용이 된 뒤로는 **없는 것을 찾으라는 말**이 됐다 — 아이폰 사파리 주소창에는 자물쇠가
   없고, 웹사이트 설정은 주소창 왼쪽 AA 버튼 안에 있다. 실제로 그 안내를 받고 못 찾았다.
   iPadOS 는 UA 를 맥으로 위장하므로 터치 개수까지 봐야 걸러진다. */
const IOS = /iP(hone|od|ad)/.test(navigator.userAgent)
  || (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/* 거절에는 관문이 둘이고 **푸는 자리가 다르다.**
   - 사이트 관문: 이 사이트에 위치를 안 준 것. 브라우저 안에서 푼다.
   - 기기 관문: OS 가 브라우저에게 위치를 안 주는 것. 설정 앱에서 푼다.
   iOS 는 기기 관문이 사이트 관문을 이긴다. 그래서 사이트 설정을 '허용'으로 켜놔도
   거절이 그대로 돌아온다 — 실제로 그 화면을 보고도 한 라운드를 더 썼다. 두 경우에
   같은 안내를 하면 반은 틀린 데를 가리키게 된다. */
const DENY_SITE = IOS
  ? "위치 권한이 막혀 있습니다 — 주소창의 AA 버튼 › 웹사이트 설정 › 위치"
  : "위치 권한이 막혀 있습니다 — 주소창의 자물쇠 › 사이트 설정 › 위치";

/* '브라우저는 허용돼 있습니다'를 먼저 말한다. 이 말이 없으면 이미 켜둔 사이트 설정을
   또 만지러 간다 — 거기는 이미 열려 있는데. */
const DENY_SYSTEM = IOS
  ? "브라우저는 허용돼 있습니다 — 설정 › 개인정보 보호 및 보안 › 위치 서비스 › Safari 웹사이트"
  : "브라우저는 허용돼 있습니다 — 기기 설정의 위치 서비스에서 브라우저를 켜주세요";

/* 설정 경로를 읽고 따라가라는 안내다. 평소 토스트(2.2초)로는 읽다가 사라진다. */
const READ_MS = 7000;

/**
 * 어느 관문이 막았나. 브라우저에게 직접 물어본다.
 *
 * 브라우저는 '이 사이트에 위치를 줄 것인가'만 안다. 그 답이 denied 면 사이트 관문이고,
 * **prompt·granted 인데도 요청이 거절당했다면** 막은 것은 브라우저 아래, 곧 기기다.
 * 설정을 하나씩 짚어보라고 하는 것보다 이게 정확하다 — 사람이 어디를 봤는지 물어보는
 * 대신 브라우저에게 사실을 묻는다.
 *
 * Permissions API 를 못 쓰면(구형 사파리) 흔한 쪽인 사이트 관문으로 안내한다.
 */
async function deniedBy() {
  try {
    const { state } = await navigator.permissions.query({ name: "geolocation" });
    return state === "denied" ? DENY_SITE : DENY_SYSTEM;
  } catch {
    return DENY_SITE;
  }
}

/* 원인을 적지 않는다. 브라우저가 주는 건 POSITION_UNAVAILABLE 하나뿐인데 그 안에
   Wi-Fi 꺼짐·실내·네트워크 차단·그냥 일시적 실패가 전부 뭉쳐 있다 — 하나를 골라 적으면
   나머지 경우에 틀린 말을 한다. 실제로 두 번 틀렸다: 'Wi-Fi 를 켜라'(켜져 있었다),
   '사내망 탓'(같은 망에서 잘 됐다). 화면에는 사실과 할 일만 남기고, 코드는 콘솔에
   남긴다(아래 console.warn). 이 오류는 대개 다시 누르면 잡힌다. */
const NO_FIX = "현위치를 찾지 못했습니다 — 잠시 뒤 다시 눌러주세요";

/* 두 번째부터는 다른 말을 한다. 한 번은 지나가는 실패일 수 있지만, 연달아 실패하면
   '다시 눌러주세요'는 나쁜 조언이다 — 못 잡는 기기에서 그 말은 무한히 다시 누르게 만든다.
   맥에서 실제로 겪었다: Wi-Fi 도 켜져 있고 애플 위치 서버도 붙는데 CoreLocation 만
   kCLErrorLocationUnknown 을 냈고, 원인은 OS 의 위치 서비스가 브라우저에 안 열린 것이었다.
   브라우저 권한과 기기 설정은 별개라, 사이트에서 허용해도 저쪽이 닫혀 있으면 못 잡는다.
   OS 이름은 안 적는다 — 같은 화면을 폰에서도 본다. */
const NO_FIX_AGAIN = "현위치를 계속 찾지 못하고 있습니다 — 기기의 위치 서비스 설정을 확인해주세요";

/**
 * 켜고 끄는 현위치 추적. pos 는 { lat, lng, acc } 이고 꺼져 있으면 null 이다.
 *
 * onFix 는 켠 뒤 **첫 좌표에만** 울린다. 지도를 옮기는 건 그 한 번이어야 한다 —
 * 갱신마다 따라가면 목록을 보려고 옮겨둔 화면을 앱이 몇 초에 한 번씩 뺏는다.
 */
export function useGeo({ onFix, onError }) {
  const [pos, setPos] = useState(null);
  const [tracking, setTracking] = useState(false);
  const watch = useRef(null);
  const giveUp = useRef(null);
  /* 켠 횟수. getCurrentPosition 은 watchPosition 과 달리 **취소할 방법이 없다** —
     껐는데 뒤늦게 도착한 거친 좌표가 점을 되살리는 걸 막으려면, 콜백이 자기가 어느 판의
     것인지 알아야 한다. 껐다 켜기를 빠르게 반복할 때 옛 판이 새 판을 덮는 것도 같이 막힌다. */
  const run = useRef(0);
  /* 연달아 몇 번 못 잡았나. 성공하면 0 으로 돌아간다 — 아침에 한 번 실패한 것 때문에
     오후에 잘 되는 사람한테까지 설정을 확인하라고 하면 안 된다. */
  const fails = useRef(0);

  /* 콜백은 ref 로 들고 있는다. 의존성에 넣으면 지도가 늦게 뜰 때 start 가 통째로 새로
     만들어지고, 그 사이 눌린 버튼은 옛 클로저(map 이 null 이던 시절)를 부른다. */
  const cb = useRef({ onFix, onError });
  useEffect(() => { cb.current = { onFix, onError }; });

  const stop = useCallback(() => {
    run.current += 1;   // 이 판은 끝났다. 뒤늦게 오는 콜백은 전부 무시된다.
    if (watch.current != null) navigator.geolocation.clearWatch(watch.current);
    watch.current = null;
    clearTimeout(giveUp.current);
    giveUp.current = null;
    setPos(null);      // 점도 같이 지운다. 꺼놓고 옛 자리에 남아 있으면 그게 거짓말이다.
    setTracking(false);
  }, []);

  const start = useCallback(() => {
    let first = true;
    const id = (run.current += 1);

    const land = (p) => {
      if (run.current !== id) return;   // 지난 판의 콜백 — 위 run 주석
      setPos({
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        acc: p.coords.accuracy || 0,
      });
      if (first) {
        first = false;
        fails.current = 0;
        clearTimeout(giveUp.current);
        giveUp.current = null;
        cb.current.onFix?.({ lat: p.coords.latitude, lng: p.coords.longitude });
      }
    };

    /* **켜진 채로 비어 있는 버튼이 제일 나쁘다** — 사용자가 보는 건 '켰는데 아무것도 안
       나온다'뿐이고 그건 고장으로 읽힌다. 좌표가 안 오면 스스로 접고 이유를 말한다.
       오류 콜백이 아니라 여기서 접는 이유는 위 GIVE_UP_MS 주석에 있다. */
    giveUp.current = setTimeout(() => {
      stop();
      fails.current += 1;
      // 두 번째부터는 설정을 보라는 안내라 읽을 시간이 필요하다.
      if (fails.current >= 2) cb.current.onError?.(NO_FIX_AGAIN, READ_MS);
      else cb.current.onError?.(NO_FIX);
    }, GIVE_UP_MS);

    /* 거친 좌표 한 방(위 COARSE 주석). 정밀한 게 이미 왔으면 버린다 — 나중에 도착해서
       더 나쁜 값으로 덮으면 점이 엉뚱한 데로 튄다. 실패는 조용히 넘긴다. 여기서 알리면
       정밀 쪽이 곧 성공하는데도 '못 찾았다'가 먼저 뜬다. 접는 건 유예 시계 하나뿐이다. */
    navigator.geolocation.getCurrentPosition(
      (p) => { if (first) land(p); }, () => {}, COARSE);

    watch.current = navigator.geolocation.watchPosition(
      land,
      (err) => {
        /* 원인을 콘솔에 남긴다. 토스트 한 줄로는 권한인지 Wi-Fi 인지 실내인지 구별이 안 돼서
           실제로 한 번 왕복했다(kCLErrorLocationUnknown 이 답이었다). 로그인 실패를 남기는
           것과 같은 이유다(App.jsx 의 signIn). */
        console.warn("geolocation", err.code, err.message);

        // 거절은 회복되지 않는다 — 유예를 줘봐야 달라질 게 없으니 바로 접는다.
        if (err.code === err.PERMISSION_DENIED) {
          stop();
          // 어느 관문인지 물어본 뒤에 말한다. 답이 오는 데 한 틱이면 된다.
          deniedBy().then((msg) => cb.current.onError?.(msg, READ_MS));
          return;
        }

        /* 나머지(위치 불명·시간 초과)는 여기서 판단하지 않는다. 곧 좌표가 따라오는 일이
           흔하고, 한 번이라도 잡은 뒤의 실패(지하·터널)는 그냥 지나가야 한다 — 그때마다
           점을 지우면 깜빡인다. 못 잡는 환경이면 위의 유예 시계가 접는다. */
      },
      OPTS);
    setTracking(true);
  }, [stop]);

  /* 상태(tracking)가 아니라 감시 id 를 보고 갈린다. setState 는 다음 렌더에야 반영되므로,
     연타하면 상태로 판단하는 쪽은 감시를 두 개 켜놓고 하나만 끄게 된다. */
  const toggle = useCallback(() => (watch.current == null ? start() : stop()), [start, stop]);

  // 화면을 떠나면 감시를 끈다. 안 끄면 GPS 가 계속 돌아 배터리를 먹는다.
  useEffect(() => stop, [stop]);

  return { pos, tracking, toggle };
}
